import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { RootLayout } from './app/RootLayout';
import { useAuthStore } from './shared/stores/authStore';
import { ErrorBoundary } from './shared/components/ErrorBoundary';

// Lazy-loaded top-level pages (reduce main chunk size)
const LandingPage = lazy(() =>
  import('./features/landing/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const PlazaPage = lazy(() =>
  import('./features/plaza/PlazaPage').then((m) => ({ default: m.PlazaPage })),
);
const CreativePanel = lazy(() =>
  import('./features/creative/CreativePanel').then((m) => ({ default: m.CreativePanel })),
);
const StudioLayout = lazy(() =>
  import('./features/studio/StudioLayout').then((m) => ({ default: m.StudioLayout })),
);
const SeedPage = lazy(() =>
  import('./features/studio/seed/SeedPage').then((m) => ({ default: m.SeedPage })),
);
const FoundationPage = lazy(() =>
  import('./features/studio/foundation/FoundationPage').then((m) => ({ default: m.FoundationPage })),
);

// Auth pages (lazy)
const LoginPage = lazy(() => import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const VerifyEmailPage = lazy(() => import('./features/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));

// Lazy-loaded Plaza sub-pages
const NovelDetailPage = lazy(() =>
  import('./features/plaza/NovelDetailPage').then((m) => ({
    default: m.NovelDetailPage,
  })),
);
const ReaderPage = lazy(() =>
  import('./features/plaza/ReaderPage').then((m) => ({
    default: m.ReaderPage,
  })),
);

// Lazy-loaded heavy pages (React Flow ~300kB, TipTap ~200kB)
const OutlinePage = lazy(() =>
  import('./features/studio/outline/OutlinePage').then((m) => ({
    default: m.OutlinePage,
  })),
);
const WritingPage = lazy(() =>
  import('./features/studio/writing/WritingPage').then((m) => ({
    default: m.WritingPage,
  })),
);
const PublishPage = lazy(() =>
  import('./features/studio/publish/PublishPage').then((m) => ({
    default: m.PublishPage,
  })),
);
const CodexPage = lazy(() =>
  import('./features/studio/codex/CodexPage').then((m) => ({
    default: m.CodexPage,
  })),
);
const ImportExportPage = lazy(() =>
  import('./features/studio/importExport/ImportExportPage').then((m) => ({
    default: m.ImportExportPage,
  })),
);

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

/** 404 page for unmatched routes */
function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <p className="text-6xl font-bold text-muted-foreground/30">404</p>
      <p className="text-lg text-muted-foreground">页面不存在</p>
      <a href="/" className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">返回首页</a>
    </div>
  );
}

/** Route guard — redirects to login if not authenticated */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  if (!initialized) return <PageFallback />;
  if (!user) return <Navigate to="/auth/login" replace />;
  return <>{children}</>;
}

export function App() {
  // Initialize auth on app start — listens for session changes
  const initialize = useAuthStore((s) => s.initialize);
  useEffect(() => { initialize(); }, [initialize]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Landing / About — 宣传页，独立布局 */}
        <Route path="about" element={<Suspense fallback={<PageFallback />}><LandingPage /></Suspense>} />

        {/* Auth — 认证页面，独立布局 (split-screen) */}
        <Route path="auth/login" element={<Suspense fallback={<PageFallback />}><LoginPage /></Suspense>} />
        <Route path="auth/register" element={<Suspense fallback={<PageFallback />}><RegisterPage /></Suspense>} />
        <Route path="auth/verify-email" element={<Suspense fallback={<PageFallback />}><VerifyEmailPage /></Suspense>} />
        <Route path="auth/forgot-password" element={<Suspense fallback={<PageFallback />}><ForgotPasswordPage /></Suspense>} />
        <Route path="auth/reset-password" element={<Suspense fallback={<PageFallback />}><ResetPasswordPage /></Suspense>} />

        {/* App Shell — 带导航的内部页面 */}
        <Route element={<RootLayout />}>
          {/* 首页 = 阅读广场 (reading mode) */}
          <Route index element={<Suspense fallback={<PageFallback />}><ErrorBoundary><PlazaPage /></ErrorBoundary></Suspense>} />
          <Route path="plaza" element={<Suspense fallback={<PageFallback />}><ErrorBoundary><PlazaPage /></ErrorBoundary></Suspense>} />
          <Route
            path="plaza/novel/:novelId"
            element={
              <Suspense fallback={<PageFallback />}>
                <NovelDetailPage />
              </Suspense>
            }
          />
          <Route
            path="plaza/novel/:novelId/read/:chapterId"
            element={
              <Suspense fallback={<PageFallback />}>
                <ReaderPage />
              </Suspense>
            }
          />

          {/* Creative Center (creative mode) — contains Workshop + Studio entry */}
          <Route path="workshop" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ErrorBoundary><CreativePanel /></ErrorBoundary></Suspense></ProtectedRoute>} />

          {/* Studio — 创作台 (requires auth) */}
          <Route path="studio/:novelId" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ErrorBoundary><StudioLayout /></ErrorBoundary></Suspense></ProtectedRoute>}>
            <Route index element={<Navigate to="seed" replace />} />
            <Route path="seed" element={<Suspense fallback={<PageFallback />}><SeedPage /></Suspense>} />
            <Route path="foundation" element={<Suspense fallback={<PageFallback />}><FoundationPage /></Suspense>} />
            <Route
              path="outline"
              element={
                <Suspense fallback={<PageFallback />}>
                  <OutlinePage />
                </Suspense>
              }
            />
            <Route
              path="writing"
              element={
                <Suspense fallback={<PageFallback />}>
                  <WritingPage />
                </Suspense>
              }
            />
            <Route
              path="codex"
              element={
                <Suspense fallback={<PageFallback />}>
                  <CodexPage />
                </Suspense>
              }
            />
            <Route
              path="publish"
              element={
                <Suspense fallback={<PageFallback />}>
                  <PublishPage />
                </Suspense>
              }
            />
            <Route
              path="import-export"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ImportExportPage />
                </Suspense>
              }
            />
          </Route>

          {/* 404 catch-all — within RootLayout shell */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* 404 catch-all — outside layout (for truly unknown paths) */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
